import {
	type AssistantConversationId,
	assistantConversationId,
	type Relations,
} from "@inrev/common";
import { assistantConversationStorageKey } from "@portal/api/assistant";
import { useCallback, useState } from "react";

/** `"new"` is a blank conversation that has no row until its first message is sent. */
export type AssistantConversationSelection = AssistantConversationId | "new";

const readStoredSelection = (key: string): AssistantConversationSelection => {
	const stored = sessionStorage.getItem(key);
	if (stored === null) return "new";
	const parsed = assistantConversationId.safeParse(stored);
	if (!parsed.success) {
		sessionStorage.removeItem(key);
		return "new";
	}
	return parsed.data;
};

/** The relation's open conversation, restored from sessionStorage and re-read when the relation changes. */
export const useStoredAssistantConversation = (relation: Relations.Notes) => {
	const key = assistantConversationStorageKey(relation);
	const [stored, setStored] = useState(() => ({ key, selection: readStoredSelection(key) }));
	const current = stored.key === key ? stored : { key, selection: readStoredSelection(key) };
	if (current !== stored) setStored(current);

	const setSelection = useCallback(
		(next: AssistantConversationSelection) => {
			if (next === "new") {
				sessionStorage.removeItem(key);
			} else {
				sessionStorage.setItem(key, next);
			}
			setStored((previous) => (previous.key === key ? { key, selection: next } : previous));
		},
		[key],
	);

	return [current.selection, setSelection] as const;
};
